// The waiting states every wallet and sign-in flow shares (2026-10-31): a
// status line that says what is happening and shows it — a turning ring
// while we work, a pulsing marigold dot while the saver has something to do
// in their wallet, a check that pops in when it is done, a caution mark when
// it is not. The look lives in landing.css ("work status"), next to the
// first-buy rows it borrows from.
//
//   workStatus(element, text, state, { steps, step })
//
// state is one of idle | working | waiting | done | error. The element keeps
// its aria-live and its text: only the text span changes, the mark is
// aria-hidden, so a screen reader hears exactly what it heard before.
//
// steps (optional) is the flow's stages in order and step the index of the
// one under way: a compact step line appears just above the status line,
// earlier stages ticked, the current one taking the state. Leave steps out
// and the step line goes away.
export const STATES = [ "idle", "working", "waiting", "done", "error" ]

const CHECK = '<svg class="work-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" ' +
  'stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>'
const MARK = `<span class="work-spinner"></span><span class="work-dot"></span>${CHECK}<span class="work-caution"></span>`

export function workStatus(element, text, state = "working", { steps, step = 0 } = {}) {
  if (!element) return
  const textNode = ensureStructure(element)
  if (textNode.textContent !== text) textNode.textContent = text
  element.dataset.workState = STATES.includes(state) ? state : "working"

  if (steps && steps.length) {
    renderSteps(element, steps, step, state)
  } else {
    removeSteps(element)
  }
}

// The button that started the action: disabled with a spinner while the
// flow runs, itself again when it stops.
export function setBusy(button, busy) {
  if (!button) return
  button.disabled = busy
  if (busy) {
    button.dataset.busy = ""
    button.setAttribute("aria-busy", "true")
  } else {
    delete button.dataset.busy
    button.removeAttribute("aria-busy")
  }
}

// An error line (the separate caution paragraph most flows have): the text,
// with the caution mark ahead of it.
export function workError(element, text) {
  if (!element) return
  element.textContent = text
  element.classList.add("work-alert")
  element.hidden = !text
}

function ensureStructure(element) {
  let textNode = element.querySelector(":scope > .work-text")
  if (textNode) return textNode

  const current = element.textContent.trim()
  element.classList.add("work-status")
  const mark = document.createElement("span")
  mark.className = "work-mark"
  mark.setAttribute("aria-hidden", "true")
  mark.innerHTML = MARK
  textNode = document.createElement("span")
  textNode.className = "work-text"
  textNode.textContent = current
  element.replaceChildren(mark, textNode)
  return textNode
}

function renderSteps(element, steps, current, state) {
  let list = stepsFor(element)
  if (!list) {
    list = document.createElement("ol")
    list.className = "work-steps"
    list.dataset.workSteps = ""
    element.before(list)
  }
  if (list.dataset.labels !== steps.join("|")) {
    list.dataset.labels = steps.join("|")
    list.replaceChildren(...steps.map((label) => {
      const item = document.createElement("li")
      const dot = document.createElement("span")
      dot.className = "work-step-dot"
      dot.setAttribute("aria-hidden", "true")
      const name = document.createElement("span")
      name.className = "work-step-label"
      name.textContent = label
      item.append(dot, name)
      return item
    }))
  }

  const finished = state === "done"
  Array.from(list.children).forEach((item, index) => {
    let stepState
    if (finished || index < current) stepState = "done"
    else if (index === current) stepState = state
    else stepState = "todo"
    item.dataset.stepState = stepState
    if (index === current && !finished) item.setAttribute("aria-current", "step")
    else item.removeAttribute("aria-current")
  })
}

function removeSteps(element) {
  stepsFor(element)?.remove()
}

function stepsFor(element) {
  const previous = element.previousElementSibling
  return previous?.matches("[data-work-steps]") ? previous : null
}
